// typescript
import { themeQuartz } from 'ag-grid-enterprise'
import i18next from '../../i18n'

const agGridLanguageFontFamilyMap: Record<string, string> = {
  en: "'Figtree', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  tw: "'Noto Sans TC', 'PingFang TC', 'Microsoft JhengHei', system-ui, sans-serif",
  cn: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', system-ui, sans-serif",
  jp: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Yu Gothic', system-ui, sans-serif",
}

const getAgGridFontFamilyForCurrentLanguage = (): string => {
  const lng = i18next.language
  return agGridLanguageFontFamilyMap[lng] ?? agGridLanguageFontFamilyMap.en
}

/**
 * Grid density modes.
 * - `compact`: dense sizing used on PC/tablet (our current look).
 * - `default`: AG Grid Quartz built-in sizing, used on mobile (more touch-friendly).
 */
export type AgGridDensity = 'compact' | 'default'

// Size params for compact mode. Default mode omits these so themeQuartz
// falls back to its built-in (larger) defaults.
const compactSizeParams = {
  spacing: 4,
  rowHeight: 28,
  headerHeight: 40,
  iconSize: 12,
}

const getAgGridSizeParams = (density: AgGridDensity) =>
  density === 'compact' ? compactSizeParams : {}

export const createDarkAgGridTheme = (density: AgGridDensity = 'compact') =>
  themeQuartz.withParams({
    accentColor: '#96D9F6',
    backgroundColor: '#31373B',
    borderColor: '#FFFFFF1F',
    browserColorScheme: 'dark',
    chromeBackgroundColor: {
      ref: 'foregroundColor',
      mix: 0.07,
      onto: 'backgroundColor',
    },
    columnBorder: false,
    fontFamily: {
      googleFont: getAgGridFontFamilyForCurrentLanguage(),
    },
    foregroundColor: '#FFFFFF',
    headerBackgroundColor: '#FFFFFF0F',
    headerFontSize: 14,
    headerRowBorder: true,
    oddRowBackgroundColor: '#31373B',
    ...getAgGridSizeParams(density),
  })

export const createLightAgGridTheme = (density: AgGridDensity = 'compact') =>
  themeQuartz.withParams({
    accentColor: '#41B2E2',
    browserColorScheme: 'light',
    fontFamily: {
      googleFont: getAgGridFontFamilyForCurrentLanguage(),
    },
    headerBackgroundColor: '#0000000A',
    headerFontSize: 14,
    ...getAgGridSizeParams(density),
  })
